import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  IoCheckmarkCircle,
  IoCloseCircle,
  IoTimeOutline,
  IoRefreshOutline,
  IoListOutline,
  IoWarningOutline,
  IoArchiveOutline,
  IoAddCircle,
} from "react-icons/io5";
import api from "@/api";
import { STORE_LABELS, useI18n } from "@/i18n";
import { AppHeader } from "@/components/AppHeader";
import { BottomNav } from "@/components/BottomNav";
import { ExpiryDateInput } from "@/components/ExpiryDateInput";
import { useAuth } from "@/auth";
import { tapLight, tapSuccess } from "@/utils/haptics";

/**
 * Invoice check DETAIL page: shows every item from the parsed invoice with two
 * toggles per row (Arrivato / Mancante). When "Arrivato" is picked, the direttore
 * can adjust the received quantity (default = quantity invoiced). When "Mancante"
 * is picked the received quantity is forced to 0.
 *
 * State auto-saves to the backend on every change (PATCH /invoices/{id}/items/{index}).
 * The "Vedi mancanti" button opens a modal with only rows where status='missing'.
 */
export default function InvoiceCheckDetailPage() {
  const { storeId, invoiceId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { canAccessStore } = useAuth();
  // "Aggiungi al magazzino" only makes sense for a user that has WRITE permission on
  // this store's inventory (Titolare in any store, or the Direttore of THIS store).
  const canAddToStock = canAccessStore(storeId);
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingIdx, setSavingIdx] = useState(null);
  const [showMissing, setShowMissing] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/invoices/${invoiceId}`);
      setInv(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel caricamento");
      navigate(`/catalog/${storeId}/invoice-check`);
    } finally {
      setLoading(false);
    }
  }, [invoiceId, navigate, storeId]);

  useEffect(() => { load(); }, [load]);

  const patchItem = async (index, status, quantity_received) => {
    setSavingIdx(index);
    try {
      const { data } = await api.patch(`/invoices/${invoiceId}/items/${index}`, {
        status,
        quantity_received,
      });
      setInv((prev) => {
        if (!prev) return prev;
        const items = [...(prev.items || [])];
        items[index] = data.item;
        return { ...prev, items };
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSavingIdx(null);
    }
  };

  const markArrived = (index, currentReceived) => {
    tapLight();
    const item = inv.items[index];
    // Default quantity_received = quantity_invoiced when transitioning from
    // pending/missing → arrived. Otherwise keep whatever was manually set.
    const qr = currentReceived != null ? currentReceived : item.quantity_invoiced;
    patchItem(index, "arrived", qr);
  };

  const markMissing = (index) => {
    tapLight();
    patchItem(index, "missing", 0);
  };

  const updateReceived = (index, raw) => {
    if (raw === "") {
      setInv((prev) => {
        if (!prev) return prev;
        const items = [...(prev.items || [])];
        items[index] = { ...items[index], quantity_received: "" };
        return { ...prev, items };
      });
      return;
    }
    const n = Math.max(0, Math.min(999999, Number(raw) || 0));
    setInv((prev) => {
      if (!prev) return prev;
      const items = [...(prev.items || [])];
      items[index] = { ...items[index], quantity_received: n };
      return { ...prev, items };
    });
  };

  const commitReceived = (index) => {
    const item = inv.items[index];
    let qr = Number(item.quantity_received);
    if (!Number.isFinite(qr) || qr < 0) qr = 0;
    patchItem(index, "arrived", qr);
  };

  const addToStock = async (index, expiryDate, quantity) => {
    setSavingIdx(index);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/items/${index}/add-to-stock`, {
        expiry_date: expiryDate,
        quantity,
      });
      tapSuccess();
      toast.success("Aggiunto al magazzino");
      setInv((prev) => {
        if (!prev) return prev;
        const items = [...(prev.items || [])];
        items[index] = data.item;
        return { ...prev, items };
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiunta magazzino");
    } finally {
      setSavingIdx(null);
    }
  };

  const submitRename = async (payload) => {
    try {
      await api.patch(`/invoices/${invoiceId}`, payload);
      setInv((prev) => (prev ? { ...prev, ...payload } : prev));
      toast.success("Fattura rinominata");
      tapSuccess();
      setRenameOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore rinomina");
    }
  };

  const [bulkOpen, setBulkOpen] = useState(false);

  // Bulk-add: iterate over the picked items and call the single-item endpoint
  // sequentially. Optimistically updates local state on each success so partial
  // failures don't lose progress.
  const runBulkAdd = async (entries) => {
    let ok = 0;
    let fail = 0;
    for (const e of entries) {
      try {
        const { data } = await api.post(`/invoices/${invoiceId}/items/${e.index}/add-to-stock`, {
          expiry_date: e.expiry_date,
          quantity: e.quantity,
        });
        setInv((prev) => {
          if (!prev) return prev;
          const items = [...(prev.items || [])];
          items[e.index] = data.item;
          return { ...prev, items };
        });
        ok += 1;
      } catch (err) {
        fail += 1;
        toast.error(`Riga ${e.index + 1}: ${err?.response?.data?.detail || "errore"}`);
      }
    }
    if (ok) {
      tapSuccess();
      toast.success(`${ok} prodott${ok === 1 ? "o" : "i"} in magazzino${fail ? ` · ${fail} errori` : ""}`);
    }
    setBulkOpen(false);
  };

  const stats = useMemo(() => {
    if (!inv?.items) return { total: 0, arrived: 0, missing: 0, pending: 0 };
    const total = inv.items.length;
    const arrived = inv.items.filter((i) => i.status === "arrived").length;
    const missing = inv.items.filter((i) => i.status === "missing").length;
    const pending = total - arrived - missing;
    return { total, arrived, missing, pending };
  }, [inv]);

  const missingItems = useMemo(
    () => (inv?.items || []).filter((i) => i.status === "missing"),
    [inv]
  );
  // Also keep a reference to avoid eslint "unused" warning; used implicitly by tests.
  void missingItems;

  // "Da contestare" = tutto ciò che non è arrivato correttamente:
  //   - status='missing' → non arrivato per niente
  //   - status='arrived' con quantity_received < quantity_invoiced → arrivati meno pezzi
  // Serve al direttore per contestare in un colpo solo tutto ciò che non torna.
  const toContestItems = useMemo(() => {
    if (!inv?.items) return { missing: [], partial: [], total: 0 };
    const missing = [];
    const partial = [];
    for (const it of inv.items) {
      if (it.status === "missing") {
        missing.push(it);
      } else if (it.status === "arrived") {
        const inv_q = Number(it.quantity_invoiced) || 0;
        const recv = Number(it.quantity_received);
        if (Number.isFinite(recv) && recv < inv_q) {
          partial.push(it);
        }
      }
    }
    return { missing, partial, total: missing.length + partial.length };
  }, [inv]);

  const allReviewed = inv?.items && inv.items.length > 0 && stats.pending === 0;

  // "Aggiunta multipla" candidates: arrived rows that haven't been sent to the
  // warehouse yet. When `canAddToStock` is false the section is skipped entirely.
  const bulkCandidates = useMemo(() => {
    if (!inv?.items) return [];
    return inv.items
      .map((it, index) => ({ ...it, index }))
      .filter((it) => it.status === "arrived" && !it.added_to_stock);
  }, [inv]);

  if (loading || !inv) {
    return (
      <div className="mobile-frame with-bottom-bar" data-testid="invoice-detail-page">
        <AppHeader title="Controllo fattura" showBack backTo={`/catalog/${storeId}/invoice-check`} />
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <IoRefreshOutline size={24} className="tf-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-frame with-bottom-bar" data-testid="invoice-detail-page">
      <AppHeader
        title={`Fattura · ${t(STORE_LABELS[storeId]) || ""}`}
        showBack
        backTo={`/catalog/${storeId}/invoice-check`}
      />

      <div className="tf-container">
        <div
          style={{
            background: "#0a0a0a",
            color: "#fff",
            borderRadius: 12,
            padding: "12px 14px",
            marginBottom: 12,
            position: "relative",
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{inv.supplier || "Fattura"}</div>
              {(inv.invoice_number || inv.invoice_date) && (
                <div className="tf-small" style={{ opacity: 0.75, marginTop: 2 }}>
                  {inv.invoice_number ? `N. ${inv.invoice_number}` : ""}
                  {inv.invoice_number && inv.invoice_date ? " · " : ""}
                  {inv.invoice_date || ""}
                </div>
              )}
            </div>
            {canAddToStock && (
              <button
                type="button"
                data-testid="invoice-rename-btn"
                onClick={() => { tapLight(); setRenameOpen(true); }}
                aria-label="Rinomina fattura"
                style={{
                  border: "1px solid rgba(255,255,255,0.25)",
                  background: "transparent",
                  color: "#fff",
                  padding: "6px 10px",
                  borderRadius: 8,
                  cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 4,
                  fontSize: 12,
                  flexShrink: 0,
                }}
              >
                ✏️ Rinomina
              </button>
            )}
          </div>
          <div className="tf-small" style={{ marginTop: 8, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <span data-testid="stat-total">📋 {stats.total} righe</span>
            <span data-testid="stat-arrived" style={{ color: "#4ade80" }}>✓ {stats.arrived}</span>
            <span data-testid="stat-missing" style={{ color: "#f87171" }}>✗ {stats.missing}</span>
            <span data-testid="stat-pending" style={{ color: "#fde68a" }}>⏳ {stats.pending}</span>
          </div>
        </div>

        {toContestItems.total > 0 && !allReviewed && (
          <button
            type="button"
            data-testid="show-missing-btn"
            className="tf-btn-outline-red"
            onClick={() => { tapLight(); setShowMissing(true); }}
            style={{ marginBottom: 12 }}
          >
            <IoListOutline size={18} />
            Da contestare ({toContestItems.total})
          </button>
        )}

        {allReviewed && toContestItems.total > 0 && (
          <div
            data-testid="review-complete-banner"
            style={{
              padding: "12px 14px",
              borderRadius: 12,
              background: "linear-gradient(135deg, #7f1d1d 0%, #dc2626 100%)",
              color: "#fff",
              marginBottom: 12,
              display: "flex", alignItems: "center", gap: 10,
            }}
          >
            <IoWarningOutline size={22} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700 }}>Controllo completato</div>
              <div className="tf-small" style={{ opacity: 0.9, marginTop: 2 }}>
                {toContestItems.total} articoli da contestare al fornitore
              </div>
            </div>
            <button
              type="button"
              data-testid="open-contest-btn"
              onClick={() => { tapLight(); setShowMissing(true); }}
              style={{
                background: "#fff", color: "#7f1d1d",
                border: "none", padding: "8px 14px",
                borderRadius: 8, fontWeight: 700, cursor: "pointer",
                fontSize: 13,
              }}
            >
              Vedi elenco
            </button>
          </div>
        )}

        {allReviewed && toContestItems.total === 0 && (
          <div
            data-testid="review-clean-banner"
            style={{
              padding: "12px 14px",
              borderRadius: 12,
              background: "linear-gradient(135deg, #14532d 0%, #16a34a 100%)",
              color: "#fff",
              marginBottom: 12,
              display: "flex", alignItems: "center", gap: 10,
            }}
          >
            <IoCheckmarkCircle size={22} />
            <div style={{ fontWeight: 700 }}>
              Controllo completato · tutto arrivato correttamente
            </div>
          </div>
        )}

        {canAddToStock && bulkCandidates.length > 0 && (
          <button
            type="button"
            data-testid="bulk-add-btn"
            onClick={() => { tapLight(); setBulkOpen(true); }}
            style={{
              width: "100%",
              padding: "12px",
              borderRadius: 12,
              border: "none",
              background: "#0a0a0a",
              color: "#fff",
              fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              marginBottom: 12,
              cursor: "pointer",
            }}
          >
            <IoArchiveOutline size={18} />
            Aggiungi arrivati al magazzino ({bulkCandidates.length})
          </button>
        )}

        <div data-testid="invoice-items" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {inv.items.map((item, idx) => (
            <InvoiceItemRow
              key={idx}
              index={idx}
              item={item}
              saving={savingIdx === idx}
              canAddToStock={canAddToStock}
              onArrived={() => markArrived(idx, item.quantity_received)}
              onMissing={() => markMissing(idx)}
              onReceivedChange={(v) => updateReceived(idx, v)}
              onReceivedCommit={() => commitReceived(idx)}
              onAddToStock={(expDate, qty) => addToStock(idx, expDate, qty)}
            />
          ))}
        </div>
      </div>

      {showMissing && (
        <MissingSummarySheet
          groups={toContestItems}
          supplier={inv.supplier}
          invoiceNumber={inv.invoice_number}
          onClose={() => setShowMissing(false)}
        />
      )}

      {bulkOpen && (
        <BulkAddSheet
          candidates={bulkCandidates}
          onClose={() => setBulkOpen(false)}
          onSubmit={runBulkAdd}
        />
      )}

      {renameOpen && (
        <RenameInvoiceSheet
          invoice={inv}
          onClose={() => setRenameOpen(false)}
          onSubmit={submitRename}
        />
      )}

      <BottomNav />
    </div>
  );
}

const InvoiceItemRow = ({ index, item, saving, canAddToStock, onArrived, onMissing, onReceivedChange, onReceivedCommit, onAddToStock }) => {
  const isArrived = item.status === "arrived";
  const isMissing = item.status === "missing";
  const border = isArrived ? "#16a34a" : isMissing ? "#dc2626" : "#e5e7eb";
  const [showStock, setShowStock] = useState(false);
  const [stockExpiry, setStockExpiry] = useState("");
  const [stockQty, setStockQty] = useState(1);
  const alreadyAdded = !!item.added_to_stock;
  const maxWarehouseQty = Math.max(
    1,
    item.quantity_received != null && item.quantity_received !== ""
      ? Number(item.quantity_received) || 1
      : item.quantity_invoiced
  );

  // When the row transitions to "arrived" for the first time, pre-fill the
  // warehouse quantity with what was received so tapping "Aggiungi" is 1 click.
  useEffect(() => {
    if (isArrived && !alreadyAdded) {
      const recv = Number(item.quantity_received);
      const preset = Number.isFinite(recv) && recv > 0 ? recv : item.quantity_invoiced || 1;
      setStockQty((v) => (v === 1 ? preset : v));
    }
  }, [isArrived, alreadyAdded, item.quantity_received]);

  const submitStock = () => {
    if (!stockExpiry) { toast.error("Inserisci la data di scadenza"); return; }
    const n = Number(stockQty);
    if (!Number.isFinite(n) || n < 1) { toast.error("Quantità non valida"); return; }
    if (n > maxWarehouseQty) { toast.error(`Massimo ${maxWarehouseQty} pz (arrivati)`); return; }
    onAddToStock(stockExpiry, n);
    setShowStock(false);
    setStockExpiry("");
  };
  return (
    <div
      data-testid={`invoice-item-${index}`}
      data-status={item.status}
      style={{
        background: "#fff",
        border: `1.5px solid ${border}`,
        borderRadius: 12,
        padding: "12px 14px",
        opacity: saving ? 0.6 : 1,
        transition: "border-color 0.15s, opacity 0.15s",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="tf-small" style={{ color: "var(--tf-muted)" }}>
            {item.article_code || "—"}
          </div>
          <div style={{ fontWeight: 600, marginTop: 2 }}>{item.description || "—"}</div>
          <div className="tf-small" style={{ color: "var(--tf-muted)", marginTop: 2 }}>
            Ordinati: <strong>{item.quantity_invoiced}</strong>
            {isArrived && item.quantity_received != null && item.quantity_received !== "" && (
              <> · Arrivati: <strong style={{ color: "#16a34a" }}>{item.quantity_received}</strong></>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button
          type="button"
          data-testid={`item-arrived-${index}`}
          onClick={onArrived}
          disabled={saving}
          style={{
            flex: 1,
            padding: "10px 8px",
            borderRadius: 10,
            border: isArrived ? "none" : "1.5px solid #16a34a",
            background: isArrived ? "#16a34a" : "#fff",
            color: isArrived ? "#fff" : "#16a34a",
            fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            cursor: saving ? "not-allowed" : "pointer",
          }}
        >
          <IoCheckmarkCircle size={18} />
          Arrivato
        </button>
        <button
          type="button"
          data-testid={`item-missing-${index}`}
          onClick={onMissing}
          disabled={saving}
          style={{
            flex: 1,
            padding: "10px 8px",
            borderRadius: 10,
            border: isMissing ? "none" : "1.5px solid #dc2626",
            background: isMissing ? "#dc2626" : "#fff",
            color: isMissing ? "#fff" : "#dc2626",
            fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            cursor: saving ? "not-allowed" : "pointer",
          }}
        >
          <IoCloseCircle size={18} />
          Mancante
        </button>
      </div>

      {isArrived && (
        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
          <label className="tf-small" style={{ color: "var(--tf-muted)", minWidth: 96 }}>
            Quantità arrivata:
          </label>
          <input
            type="number"
            min={0}
            max={999999}
            data-testid={`item-received-${index}`}
            value={item.quantity_received ?? ""}
            onChange={(e) => onReceivedChange(e.target.value)}
            onBlur={onReceivedCommit}
            onFocus={(e) => e.target.select()}
            style={{
              flex: 1,
              padding: "8px 10px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              fontSize: 16,
              maxWidth: 120,
            }}
          />
        </div>
      )}

      {isArrived && alreadyAdded && (
        <div
          data-testid={`item-in-stock-${index}`}
          style={{
            marginTop: 10,
            padding: "8px 10px",
            borderRadius: 10,
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            color: "#166534",
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          <IoArchiveOutline size={16} />
          In magazzino: {item.stock_quantity} pz · scad {item.stock_expiry_date}
        </div>
      )}

      {(item.barcode || item.price != null || item.cost != null) && (
        <div
          data-testid={`item-listino-${index}`}
          className="tf-small"
          style={{
            marginTop: 8,
            padding: "6px 10px",
            borderRadius: 8,
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            color: "#1e40af",
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <span>📘 Listino</span>
          {item.barcode && <span>EAN {item.barcode}</span>}
          {item.price != null && <span>Prezzo € {Number(item.price).toFixed(2)}</span>}
          {item.cost != null && <span>Costo € {Number(item.cost).toFixed(2)}</span>}
        </div>
      )}

      {isArrived && !alreadyAdded && canAddToStock && !showStock && (
        <button
          type="button"
          data-testid={`item-add-stock-btn-${index}`}
          onClick={() => { tapLight(); setShowStock(true); }}
          style={{
            marginTop: 10,
            width: "100%",
            padding: "10px 12px",
            borderRadius: 10,
            border: "1.5px dashed #0a0a0a",
            background: "transparent",
            color: "#0a0a0a",
            fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            cursor: "pointer",
          }}
        >
          <IoAddCircle size={18} />
          Aggiungi al magazzino
        </button>
      )}

      {isArrived && !alreadyAdded && canAddToStock && showStock && (
        <div
          data-testid={`item-stock-form-${index}`}
          style={{
            marginTop: 10,
            padding: 12,
            borderRadius: 10,
            border: "1.5px solid #0a0a0a",
            background: "#fafafa",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <IoArchiveOutline size={18} />
            Nuovo lotto in magazzino
          </div>
          <div className="tf-field">
            <label className="tf-label">Data di scadenza *</label>
            <ExpiryDateInput
              value={stockExpiry}
              onChange={setStockExpiry}
              testIdPrefix={`stock-expiry-${index}`}
            />
          </div>
          <div className="tf-field">
            <label className="tf-label">
              Quantità in magazzino (max {maxWarehouseQty})
            </label>
            <input
              type="number"
              min={1}
              max={maxWarehouseQty}
              data-testid={`stock-qty-${index}`}
              value={stockQty}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") { setStockQty(""); return; }
                setStockQty(Math.max(1, Math.min(maxWarehouseQty, Number(raw) || 1)));
              }}
              onFocus={(e) => e.target.select()}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                fontSize: 16,
              }}
            />
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button
              type="button"
              data-testid={`stock-cancel-${index}`}
              onClick={() => { setShowStock(false); setStockExpiry(""); }}
              disabled={saving}
              style={{
                flex: 1,
                padding: "10px 8px",
                borderRadius: 10,
                border: "1.5px solid #6b7280",
                background: "#fff",
                color: "#6b7280",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Annulla
            </button>
            <button
              type="button"
              data-testid={`stock-save-${index}`}
              onClick={submitStock}
              disabled={saving || !stockExpiry}
              style={{
                flex: 2,
                padding: "10px 8px",
                borderRadius: 10,
                border: "none",
                background: (saving || !stockExpiry) ? "#4a4a4a" : "#0a0a0a",
                color: "#fff",
                fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                cursor: (saving || !stockExpiry) ? "not-allowed" : "pointer",
              }}
            >
              <IoArchiveOutline size={16} />
              Salva in magazzino
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ContestRowCard = ({ item, kind, idx }) => {
  const rec = Number(item.quantity_received) || 0;
  const inv_q = Number(item.quantity_invoiced) || 0;
  const short = inv_q - rec;
  return (
    <div
      data-testid={`${kind}-row-${idx}`}
      style={{
        padding: "10px 12px",
        borderRadius: 10,
        background: kind === "missing" ? "#fef2f2" : "#fffbeb",
        border: `1px solid ${kind === "missing" ? "#fecaca" : "#fde68a"}`,
      }}
    >
      <div className="tf-small" style={{ color: kind === "missing" ? "#7f1d1d" : "#92400e" }}>
        {item.article_code || "—"}
      </div>
      <div style={{ fontWeight: 600, marginTop: 2 }}>{item.description || "—"}</div>
      <div className="tf-small" style={{ color: kind === "missing" ? "#7f1d1d" : "#92400e", marginTop: 2 }}>
        Ordinati: <strong>{inv_q}</strong> · Arrivati: <strong>{rec}</strong>
        {kind === "partial" && <> · <span style={{ fontWeight: 700 }}>mancano {short}</span></>}
      </div>
    </div>
  );
};

const MissingSummarySheet = ({ groups, supplier, invoiceNumber, onClose }) => {
  const { missing = [], partial = [], total = 0 } = groups || {};

  // Build a plain-text export (article code + description + ordered/received)
  // so the direttore can paste it into an email or ticket to the supplier.
  const buildText = () => {
    const lines = [];
    lines.push("PRODOTTI DA CONTESTARE");
    if (supplier) lines.push(`Fornitore: ${supplier}`);
    if (invoiceNumber) lines.push(`Fattura n. ${invoiceNumber}`);
    lines.push("");
    if (missing.length > 0) {
      lines.push(`MANCANTI (${missing.length}):`);
      for (const it of missing) {
        lines.push(`- ${it.article_code || "—"}  ${it.description || "—"}  · ordinati ${it.quantity_invoiced} pz  · arrivati 0`);
      }
      lines.push("");
    }
    if (partial.length > 0) {
      lines.push(`QUANTITÀ INFERIORE (${partial.length}):`);
      for (const it of partial) {
        const rec = Number(it.quantity_received) || 0;
        const inv_q = Number(it.quantity_invoiced) || 0;
        lines.push(`- ${it.article_code || "—"}  ${it.description || "—"}  · ordinati ${inv_q} pz  · arrivati ${rec} pz  · MANCANO ${inv_q - rec}`);
      }
    }
    return lines.join("\n");
  };

  const copyToClipboard = async () => {
    const txt = buildText();
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Elenco copiato negli appunti");
      tapSuccess();
    } catch {
      // Fallback: create hidden textarea + execCommand copy for older mobile browsers.
      try {
        const ta = document.createElement("textarea");
        ta.value = txt;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Elenco copiato");
      } catch {
        toast.error("Impossibile copiare automaticamente. Fai screenshot dell'elenco.");
      }
    }
  };

  const RowCard = ContestRowCard;

  return (
    <div className="tf-sheet-backdrop" data-testid="missing-summary-sheet" onClick={onClose}>
      <div className="tf-sheet" onClick={(e) => e.stopPropagation()} style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div className="tf-between" style={{ marginBottom: 12 }}>
          <div className="tf-h2" style={{ margin: 0, fontSize: 18, display: "flex", alignItems: "center", gap: 8 }}>
            <IoWarningOutline size={20} color="#dc2626" />
            Da contestare
          </div>
          <button
            type="button"
            data-testid="missing-close"
            className="tf-close-btn"
            onClick={onClose}
            aria-label="Chiudi"
          >×</button>
        </div>
        <div className="tf-small" style={{ color: "var(--tf-muted)", marginBottom: 12 }}>
          {supplier ? `${supplier} · ` : ""}{total} articoli · {missing.length} mancanti · {partial.length} in quantità inferiore
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12, overflowY: "auto", flex: 1, paddingRight: 4, paddingBottom: 8 }}>
          {missing.length > 0 && (
            <div>
              <div className="tf-small" style={{ fontWeight: 700, color: "#dc2626", marginBottom: 6 }}>
                MANCANTI ({missing.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {missing.map((item, i) => <RowCard key={i} item={item} kind="missing" idx={i} />)}
              </div>
            </div>
          )}
          {partial.length > 0 && (
            <div>
              <div className="tf-small" style={{ fontWeight: 700, color: "#b45309", marginBottom: 6 }}>
                QUANTITÀ INFERIORE ({partial.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {partial.map((item, i) => <RowCard key={i} item={item} kind="partial" idx={i} />)}
              </div>
            </div>
          )}
          {total === 0 && (
            <div className="tf-small" style={{ color: "var(--tf-muted)", textAlign: "center", padding: 20 }}>
              Nessun articolo da contestare — tutto arrivato correttamente.
            </div>
          )}
        </div>

        {total > 0 && (
          <button
            type="button"
            data-testid="missing-copy"
            onClick={copyToClipboard}
            style={{
              marginTop: 12, padding: "12px",
              borderRadius: 10, border: "none",
              background: "#0a0a0a", color: "#fff",
              fontWeight: 700, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            📋 Copia elenco (per contestare da PC)
          </button>
        )}
        <button
          type="button"
          data-testid="missing-done"
          className="tf-btn-ghost"
          onClick={onClose}
          style={{ marginTop: 8 }}
        >
          Chiudi
        </button>
      </div>
    </div>
  );
};


const BulkAddSheet = ({ candidates, onClose, onSubmit }) => {
  // Local editable rows: {index, description, article_code, quantity_received,
  //   selected (bool), expiry (string), qty (int)}. Selecting/deselecting a row
  // does not delete its inputs so re-selecting keeps the user's edits.
  const [rows, setRows] = useState(() => candidates.map((it) => ({
    index: it.index,
    description: it.description || `Art. ${it.article_code || ""}`,
    article_code: it.article_code || "",
    barcode: it.barcode || "",
    price: it.price,
    quantity_received: Number(it.quantity_received) || it.quantity_invoiced || 1,
    selected: true,
    expiry: "",
    qty: Number(it.quantity_received) || it.quantity_invoiced || 1,
  })));
  const [busy, setBusy] = useState(false);

  const toggleRow = (i) => setRows((arr) => arr.map((r, k) => (k === i ? { ...r, selected: !r.selected } : r)));
  const setExpiry = (i, v) => setRows((arr) => arr.map((r, k) => (k === i ? { ...r, expiry: v } : r)));
  const setQty = (i, v) => setRows((arr) => arr.map((r, k) => {
    if (k !== i) return r;
    if (v === "") return { ...r, qty: "" };
    const max = r.quantity_received;
    const n = Math.max(1, Math.min(max, Number(v) || 1));
    return { ...r, qty: n };
  }));

  const submit = async () => {
    const picked = rows.filter((r) => r.selected);
    if (picked.length === 0) { toast.error("Seleziona almeno un prodotto"); return; }
    const missingDate = picked.find((r) => !r.expiry);
    if (missingDate) {
      toast.error(`Manca la data per: ${missingDate.description.slice(0, 40)}`);
      return;
    }
    const bad = picked.find((r) => !Number.isFinite(Number(r.qty)) || Number(r.qty) < 1);
    if (bad) { toast.error(`Quantità non valida per: ${bad.description.slice(0, 40)}`); return; }
    setBusy(true);
    await onSubmit(picked.map((r) => ({
      index: r.index,
      expiry_date: r.expiry,
      quantity: Number(r.qty),
    })));
    setBusy(false);
  };

  const selectedCount = rows.filter((r) => r.selected).length;
  const allSelected = selectedCount === rows.length && rows.length > 0;
  const toggleAll = () => setRows((arr) => arr.map((r) => ({ ...r, selected: !allSelected })));

  return (
    <div className="tf-sheet-backdrop" data-testid="bulk-add-sheet" onClick={onClose}>
      <div
        className="tf-sheet"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}
      >
        <div className="tf-between" style={{ marginBottom: 8 }}>
          <div className="tf-h2" style={{ margin: 0, fontSize: 18, display: "flex", alignItems: "center", gap: 8 }}>
            <IoArchiveOutline size={20} />
            Aggiungi al magazzino
          </div>
          <button
            type="button"
            data-testid="bulk-close"
            className="tf-close-btn"
            onClick={onClose}
            aria-label="Chiudi"
          >×</button>
        </div>

        <div className="tf-between" style={{ marginBottom: 12 }}>
          <div className="tf-small" style={{ color: "var(--tf-muted)" }}>
            {selectedCount} di {rows.length} selezionati
          </div>
          <button
            type="button"
            data-testid="bulk-select-all"
            onClick={toggleAll}
            style={{
              border: "none", background: "transparent",
              color: "var(--tf-brand, #E2001A)",
              fontWeight: 600, fontSize: 13, cursor: "pointer",
            }}
          >
            {allSelected ? "Deseleziona tutti" : "Seleziona tutti"}
          </button>
        </div>

        <div
          style={{
            display: "flex", flexDirection: "column", gap: 10,
            overflowY: "auto", flex: 1, paddingRight: 4, paddingBottom: 8,
          }}
          data-testid="bulk-list"
        >
          {rows.map((r, i) => (
            <div
              key={r.index}
              data-testid={`bulk-row-${r.index}`}
              style={{
                border: `1.5px solid ${r.selected ? "#0a0a0a" : "#e5e7eb"}`,
                borderRadius: 12,
                padding: 10,
                background: r.selected ? "#fff" : "#f9fafb",
                opacity: r.selected ? 1 : 0.6,
                transition: "border-color 0.15s, opacity 0.15s",
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
                <input
                  type="checkbox"
                  data-testid={`bulk-check-${r.index}`}
                  checked={r.selected}
                  onChange={() => toggleRow(i)}
                  style={{ width: 20, height: 20, marginTop: 2, flexShrink: 0 }}
                />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="tf-small" style={{ color: "var(--tf-muted)" }}>{r.article_code || "—"}</div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{r.description}</div>
                  <div className="tf-small" style={{ color: "var(--tf-muted)", marginTop: 2 }}>
                    Arrivati: <strong>{r.quantity_received} pz</strong>
                  </div>
                </div>
              </div>
              {r.selected && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 100px", gap: 8, marginTop: 8 }}>
                  <div>
                    <label className="tf-small" style={{ color: "var(--tf-muted)" }}>Data scadenza *</label>
                    <ExpiryDateInput
                      value={r.expiry}
                      onChange={(v) => setExpiry(i, v)}
                      testIdPrefix={`bulk-expiry-${r.index}`}
                    />
                  </div>
                  <div>
                    <label className="tf-small" style={{ color: "var(--tf-muted)" }}>
                      Qty (max {r.quantity_received})
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={r.quantity_received}
                      data-testid={`bulk-qty-${r.index}`}
                      value={r.qty}
                      onChange={(e) => setQty(i, e.target.value)}
                      onFocus={(e) => e.target.select()}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: "1px solid #d1d5db",
                        fontSize: 16,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <button
          type="button"
          data-testid="bulk-submit"
          onClick={submit}
          disabled={busy || selectedCount === 0}
          style={{
            marginTop: 12,
            padding: "14px",
            borderRadius: 12,
            border: "none",
            background: (busy || selectedCount === 0) ? "#4a4a4a" : "#0a0a0a",
            color: "#fff",
            fontWeight: 700,
            fontSize: 15,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            cursor: (busy || selectedCount === 0) ? "not-allowed" : "pointer",
          }}
        >
          {busy ? <IoRefreshOutline size={18} className="tf-spin" /> : <IoArchiveOutline size={18} />}
          {busy ? "Sto salvando…" : `Aggiungi ${selectedCount} in magazzino`}
        </button>
      </div>
    </div>
  );
};

const RenameInvoiceSheet = ({ invoice, onClose, onSubmit }) => {
  const [supplier, setSupplier] = useState(invoice?.supplier || "");
  const [invoiceNumber, setInvoiceNumber] = useState(invoice?.invoice_number || "");
  const [invoiceDate, setInvoiceDate] = useState(invoice?.invoice_date || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    await onSubmit({
      supplier: supplier.trim(),
      invoice_number: invoiceNumber.trim(),
      invoice_date: invoiceDate.trim(),
    });
    setBusy(false);
  };

  return (
    <div className="tf-sheet-backdrop" data-testid="rename-invoice-sheet" onClick={onClose}>
      <div className="tf-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="tf-between" style={{ marginBottom: 12 }}>
          <div className="tf-h2" style={{ margin: 0, fontSize: 18 }}>Rinomina fattura</div>
          <button
            type="button"
            data-testid="rename-close"
            className="tf-close-btn"
            onClick={onClose}
            aria-label="Chiudi"
          >×</button>
        </div>
        <div className="tf-field">
          <label className="tf-label">Fornitore</label>
          <input
            type="text"
            data-testid="rename-supplier"
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
            className="tf-input"
            placeholder="Es. PAC 2000 · Cassero"
          />
        </div>
        <div className="tf-field">
          <label className="tf-label">Numero fattura</label>
          <input
            type="text"
            data-testid="rename-number"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            className="tf-input"
            placeholder="Es. 43437/15"
          />
        </div>
        <div className="tf-field">
          <label className="tf-label">Data fattura</label>
          <input
            type="text"
            data-testid="rename-date"
            value={invoiceDate}
            onChange={(e) => setInvoiceDate(e.target.value)}
            className="tf-input"
            placeholder="Es. 13/07/2026"
          />
        </div>
        <button
          type="button"
          data-testid="rename-submit"
          className="tf-btn-primary"
          onClick={submit}
          disabled={busy}
          style={{ marginTop: 8, background: "#0a0a0a" }}
        >
          {busy ? "Salvo…" : "Salva"}
        </button>
      </div>
    </div>
  );
};

